import { datoToken, datoEnvironment as datoEnvironmentConfig } from 'js/config'
import { isSSR } from 'js/isSSR'
import languageToDatoLocale, { datoLocaleToLanguage } from './datoLocales'
import { LanguageCodes, languages, languageSlugAliases } from './locale'

function datoEnvironment() {
  if (isSSR) return ''

  const urlEnvironment = new URLSearchParams(window.location.search).get(
    'environment',
  )
  // `?environment=` with no value → production copy
  // ?environment=staging-env → staging copy
  // no parameter → whatever the build's config says
  if (urlEnvironment !== null) {
    return urlEnvironment ? `/environments/${urlEnvironment}` : ''
  }

  if (datoEnvironmentConfig) {
    return `/environments/${datoEnvironmentConfig}`
  }

  return ''
}

export function search(query: string, limit = 20, offset = 0) {
  return fetch(
    `https://site-api.datocms.com/search-results?q=${query}&limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      headers: {
        Authorization: 'API-Token c32e5b8bde3bebefe4ede3bdc45364',
        Accept: 'application/json',
      },
    },
  )
    .then((res) => res.json())
    .then((json) => {
      if (!json.errors) return json
      throw json.errors
    })
}

export function runQuery(query: string, variables?: {}, isPreview?: boolean) {
  const TOKEN = datoToken

  return fetch(
    isPreview
      ? `https://graphql.datocms.com${datoEnvironment()}/preview`
      : `https://graphql.datocms.com${datoEnvironment()}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${TOKEN}`,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    },
  )
    .then((res) => res.json())
    .then((json) => {
      if (!json.errors) return json.data
      throw json.errors
    })
    .catch((error) => {
      console.error(
        'QUERY ERROR',
        error.length && error.map((error) => error.message).join('\n'),
        'on query',
        query,
        'Errors: ',
        error,
      )
      throw error
    })
}

export function mergeFallback(page: {}, fallback: {}) {
  const mergedPage = { ...page }

  Object.keys(page).forEach((key) => {
    if (page[key] === '') {
      mergedPage[key] = fallback[key]
    }

    if (Array.isArray(page[key]) && page[key].length === 0) {
      mergedPage[key] = fallback[key]
    }
  })

  return mergedPage
}

/**
 * Converts a record's Dato `_locales` (SiteLocale values such as `pt_BR`) into
 * the app's own language codes (`pt`), dropping any that don't map. Passing raw
 * Dato locales to the language switcher would feed it codes the rest of the app
 * doesn't recognise (e.g. selecting `pt_BR` rejects in `setLanguage` and builds
 * an invalid `/pt_BR/...` URL).
 */
export function datoLocalesToLanguages(
  locales: readonly string[] = [],
): LanguageCodes[] {
  return locales
    .map(
      (locale) =>
        datoLocaleToLanguage[locale as keyof typeof datoLocaleToLanguage],
    )
    .filter((language): language is LanguageCodes => Boolean(language))
}

type SupportedLanguageInput = LanguageCodes | keyof typeof languageSlugAliases

export function getDatoLocale(language: SupportedLanguageInput) {
  const normalizedLanguage =
    languageSlugAliases[language] ?? (language as LanguageCodes)

  if (!languages.includes(normalizedLanguage)) {
    return 'en'
  }

  return (
    languageToDatoLocale[
      normalizedLanguage as keyof typeof languageToDatoLocale
    ] ?? 'en'
  )
}
